import * as React from "react";
import clsx from "clsx";
import { CheckOutlined } from "@ant-design/icons";
import { theme } from "antd";

export type ChartType = "timeSeriesLineChart" | "columnChart";

export type ChartTypesValue = [ChartType | null, ChartType | null];

type LibraryItem = {
  key: ChartType;
  image: string;
  title: string;
  subtitle: string;
};

const ITEMS: LibraryItem[] = [
  {
    key: "timeSeriesLineChart",
    image: "/assets/Time Series Line Chart.png",
    title: "Line Chart",
    subtitle:
      "Displays share price on a daily basis over a selected time period for a primary company/index and selected peers.",
  },
  {
    key: "columnChart",
    image: "/assets/Column Chart.png",
    title: "Column Chart",
    subtitle: "Displays share price performance between two selected time periods for a primary company and selected peers.",
  },
];

export type ChartTypeSelectorProps = {
  chartCount: 1 | 2 | null;
  value: ChartTypesValue;
  onChange: (index: 0 | 1, next: ChartType) => void;
};

export const ChartTypeSelector: React.FC<ChartTypeSelectorProps> = ({ chartCount, value, onChange }) => {
  const { token } = theme.useToken();
  const buttonToken = (token as { Button?: { colorPrimary?: string; colorPrimaryHover?: string } }).Button;
  const primaryColor = buttonToken?.colorPrimary ?? token.colorPrimary;
  const primaryHover = buttonToken?.colorPrimaryHover ?? token.colorPrimaryHover ?? primaryColor;

  const selectedAccentStyle = React.useMemo(
    () =>
      ({
        ["--precedent-primary"]: primaryColor,
        ["--precedent-primary-hover"]: primaryHover,
      }) as React.CSSProperties,
    [primaryColor, primaryHover]
  );

  if (!chartCount) {
    return <p className="text-gray-500 text-xs">Select a slide layout first.</p>;
  }

  const blocks = chartCount === 2 ? ([0, 1] as const) : ([0] as const);

  return (
    <div className="flex flex-col gap-2">
      {blocks.map((idx) => (
        <div key={idx} className=" bg-white p-4 ">
          <div className="mb-3 text-base font-bold text-gray-800">
            {chartCount === 2 ? `Chart Placeholder (#${idx + 1})` : "Chart Placeholder (#1)"}
          </div>

          <div className="flex w-full min-w-0 gap-3">
            {ITEMS.map((item) => {
              const selected = value[idx] === item.key;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => onChange(idx, item.key)}
                  style={selected ? selectedAccentStyle : undefined}
                  aria-pressed={selected}
                  className={clsx(
                    "group relative min-w-0 flex-1 overflow-hidden rounded-md border-2 bg-gray-50 text-left transition-[box-shadow,border-color] duration-[160ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
                    selected
                      ? "border-[color:var(--precedent-primary)] shadow-md hover:border-[color:var(--precedent-primary-hover)] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] focus-visible:ring-[color:var(--precedent-primary)]"
                      : "border-gray-200 shadow-sm hover:border-[#ff6a00] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] focus-visible:ring-gray-500"
                  )}
                >
                  {selected ? (
                    <>
                      <div
                        className="pointer-events-none absolute inset-0 z-[1] rounded-md"
                        style={{
                          backgroundColor: "color-mix(in srgb, var(--precedent-primary) 8%, transparent)",
                        }}
                        aria-hidden
                      />
                      <div
                        className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-black/10 opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
                        aria-hidden
                      />
                      <div
                        className="pointer-events-none absolute right-3 top-3 z-[3] flex h-5 w-5 items-center justify-center rounded-full text-white shadow-md"
                        style={{ backgroundColor: "var(--precedent-primary)" }}
                        aria-hidden
                      >
                        <CheckOutlined className="!text-[13px] leading-none" />
                      </div>
                    </>
                  ) : (
                    <div
                      className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-[rgba(0,0,0,0.09)] opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
                      aria-hidden
                    />
                  )}

                  <div className="relative z-0">
                    <div className="box-border px-2.5 pt-2.5 pb-1">
                      <div className="flex w-full items-center justify-center overflow-hidden rounded-sm bg-gray-50">
                        <img
                          alt={item.title}
                          src={item.image}
                          loading="lazy"
                          decoding="async"
                          className="max-h-[188px] max-w-[95%] object-contain"
                          style={{ imageRendering: "auto" }}
                        />
                      </div>
                    </div>
                    <div className="px-3 pb-2.5 pt-1">
                      <div className="mb-1 font-semibold text-gray-800">{item.title}</div>
                        <div className="h-[3em] overflow-hidden text-ellipsis text-gray-600 [word-break:break-word]">
                          {item.subtitle}
                        </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

